export const TAU = Math.PI * 2;

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function smoothstep(e0: number, e1: number, x: number): number {
  const t = clamp((x - e0) / (e1 - e0), 0, 1);
  return t * t * (3 - 2 * t);
}

/** Wraps an angle to (-π, π]. */
export function wrapAngle(a: number): number {
  a = a % TAU;
  if (a <= -Math.PI) a += TAU;
  else if (a > Math.PI) a -= TAU;
  return a;
}

export function lerpAngle(a: number, b: number, t: number): number {
  return a + wrapAngle(b - a) * t;
}

export function approach(v: number, target: number, maxDelta: number): number {
  if (v < target) return Math.min(v + maxDelta, target);
  return Math.max(v - maxDelta, target);
}

/** Deterministic 32-bit integer hash → [0, 1). */
export function hash2(ix: number, iz: number, seed: number): number {
  let h = (ix * 374761393 + iz * 668265263 + seed * 2147483647) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

/** Smooth value noise in [-1, 1]. */
export function valueNoise(x: number, z: number, seed: number): number {
  const ix = Math.floor(x);
  const iz = Math.floor(z);
  const fx = x - ix;
  const fz = z - iz;
  const ux = fx * fx * (3 - 2 * fx);
  const uz = fz * fz * (3 - 2 * fz);
  const a = hash2(ix, iz, seed);
  const b = hash2(ix + 1, iz, seed);
  const c = hash2(ix, iz + 1, seed);
  const d = hash2(ix + 1, iz + 1, seed);
  return (lerp(lerp(a, b, ux), lerp(c, d, ux), uz)) * 2 - 1;
}

/** Seeded PRNG (mulberry32) for server-side choices that tests can pin. */
export function createRng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Pose of the ship on the flat sea plane. Heading 0 faces +z; positive heading turns toward +x. */
export interface Pose {
  x: number;
  z: number;
  heading: number;
}

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

/** Ship-local (lx, lz) → world, written into `out`. */
export function localToWorld(pose: Pose, lx: number, ly: number, lz: number, out: Vec3): Vec3 {
  const c = Math.cos(pose.heading);
  const s = Math.sin(pose.heading);
  out.x = pose.x + lx * c + lz * s;
  out.y = ly;
  out.z = pose.z - lx * s + lz * c;
  return out;
}

/** World → ship-local, written into `out`. */
export function worldToLocal(pose: Pose, wx: number, wy: number, wz: number, out: Vec3): Vec3 {
  const c = Math.cos(pose.heading);
  const s = Math.sin(pose.heading);
  const dx = wx - pose.x;
  const dz = wz - pose.z;
  out.x = dx * c - dz * s;
  out.y = wy;
  out.z = dx * s + dz * c;
  return out;
}

export function dist2d(ax: number, az: number, bx: number, bz: number): number {
  const dx = ax - bx;
  const dz = az - bz;
  return Math.sqrt(dx * dx + dz * dz);
}
